import { z } from 'zod';
import { DismissedProposalSchema } from './application-proposal.js';
import { IdSchema, IsoDateTimeSchema, OrgIdSchema, UserIdSchema, VersionSchema } from './common.js';
/**
 * A connection to a customer's AWS account, for discovering its architecture.
 *
 * The connection holds an access key for an IAM user the customer creates with only the
 * discovery policy (`domain/cloud-discovery.ts`). The key itself is never part of this entity:
 * it is written to Secrets Manager on the way in and can be read back by exactly one thing — the
 * discovery worker. Nothing the API returns, logs or audits contains it (ADR 0005).
 */
export const CLOUD_CONNECTION_MAX_REGIONS = 10;
/** An AWS region name such as `ap-southeast-2` or `us-gov-west-1`. */
export const AwsRegionSchema = z
    .string()
    .regex(/^[a-z]{2}(-gov|-iso[a-z]?)?-[a-z]+-\d$/, 'Not an AWS region name');
/**
 * A long-term IAM user access key id. Temporary keys (`ASIA…`) are refused: they expire within
 * hours and need a session token, so a connection built on one would silently stop working.
 */
export const AccessKeyIdSchema = z
    .string()
    .trim()
    .regex(/^AKIA[A-Z0-9]{16}$/, 'Must be an IAM user access key id (AKIA…)');
export const SecretAccessKeySchema = z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9/+=]{40}$/, 'Must be a 40-character secret access key');
/**
 * `PENDING` — stored, not yet checked. `VERIFIED` — the key works and simulation shows it can do
 * none of the sensitive actions. `REJECTED` — it failed one of those; the reasons say which.
 * `REVOKED` — the key was deleted from the platform; the row stays for the audit trail.
 */
export const CloudConnectionStatusSchema = z.enum(['PENDING', 'VERIFIED', 'REJECTED', 'REVOKED']);
export const CloudConnectionSchema = z.object({
    id: IdSchema,
    orgId: OrgIdSchema,
    provider: z.literal('AWS'),
    name: z.string().max(80),
    regions: z.array(AwsRegionSchema).max(CLOUD_CONNECTION_MAX_REGIONS),
    status: CloudConnectionStatusSchema,
    /** The last four characters of the key id — enough to tell keys apart, useless to an attacker. */
    keyFingerprint: z.string().length(4),
    /** Filled in by verification, from `sts:GetCallerIdentity`. */
    accountId: z
        .string()
        .regex(/^\d{12}$/)
        .optional(),
    principalArn: z.string().max(2048).optional(),
    /** When the key was created in IAM, so the console can say it is due for rotation. */
    keyCreatedAt: IsoDateTimeSchema.optional(),
    verifiedAt: IsoDateTimeSchema.optional(),
    /** Why verification refused the key, in words for the administrator. */
    rejectionReasons: z.array(z.string().max(300)).max(60).default([]),
    /** Discovery actions the key lacks. A warning, not a refusal: those services go undiscovered. */
    missingActions: z.array(z.string().max(100)).max(200).default([]),
    /** Application proposals someone decided are not applications (ADR 0008). */
    dismissedProposals: z.array(DismissedProposalSchema).max(500).default([]),
    createdAt: IsoDateTimeSchema,
    createdBy: UserIdSchema,
    updatedAt: IsoDateTimeSchema,
    version: VersionSchema,
});
export const ListCloudConnectionsResponseSchema = z.object({
    items: z.array(CloudConnectionSchema),
});
const regionsField = z
    .array(AwsRegionSchema)
    .min(1)
    .max(CLOUD_CONNECTION_MAX_REGIONS)
    .refine((r) => new Set(r).size === r.length, 'Regions contain duplicates');
/** POST /v1/cloud-connections — security-admin only. The key is accepted here and never returned. */
export const CreateCloudConnectionRequestSchema = z.strictObject({
    name: z.string().trim().min(1).max(80),
    regions: regionsField,
    accessKeyId: AccessKeyIdSchema,
    secretAccessKey: SecretAccessKeySchema,
});
/** POST /v1/cloud-connections/{id}/rotate — replaces the stored key, and re-verifies. */
export const RotateCloudConnectionRequestSchema = z.strictObject({
    expectedVersion: VersionSchema,
    accessKeyId: AccessKeyIdSchema,
    secretAccessKey: SecretAccessKeySchema,
});
/** POST /v1/cloud-connections/{id}/verify and /revoke. */
export const CloudConnectionActionRequestSchema = z.strictObject({
    expectedVersion: VersionSchema,
});
/** A key older than this is flagged for rotation in the console. */
export const KEY_ROTATION_DAYS = 90;
//# sourceMappingURL=cloud-connection.js.map